import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { saveLenderSettings } from '../api/lenderSettingsApi';
import { useNotifications } from '../context/NotificationContext';

const AddLenderModal = ({ show, onHide, onLenderCreated }) => {
  // Form state
  const [formData, setFormData] = useState({
    lenderName: '',
    financingType: '',
    tier: 3,
    active: true,
    emailOnly: true,
    emailToAddress: '',
    emailCcAddress: '',
    emailSubject: 'New Submission For Review - {businessName}',
    emailBody: 'Hello {lenderName},\n\nPlease see the attached submission with supporting documentation for review. Thank you.\n\n{dealNotes}\n\nSubmission Notes:\n\nThank You,\nOperations Support'
  });
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  
  // Notifications
  const { error: notifyError } = useNotifications();
  
  /**
   * Handle form field changes
   */
  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear field-specific error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null
      }));
    }
  };
  
  /**
   * Validate form data
   */
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.lenderName?.trim()) {
      newErrors.lenderName = 'Lender name is required';
    }
    
    if (!formData.financingType) {
      newErrors.financingType = 'Financing type is required';
    }
    
    if (formData.emailOnly && !formData.emailToAddress?.trim()) {
      newErrors.emailToAddress = 'Email address is required for email lenders';
    }
    
    if (formData.emailToAddress && !isValidEmail(formData.emailToAddress)) {
      newErrors.emailToAddress = 'Please enter a valid email address';
    }
    
    if (formData.emailCcAddress && !isValidEmailList(formData.emailCcAddress)) {
      newErrors.emailCcAddress = 'Please enter valid email addresses separated by semicolons';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * Validate single email address
   */
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };
  
  /**
   * Validate list of email addresses separated by semicolons
   */
  const isValidEmailList = (emailList) => {
    if (!emailList.trim()) return true; // Empty is valid for optional field
    
    const emails = emailList.split(';').map(email => email.trim()).filter(email => email);
    return emails.every(email => isValidEmail(email));
  };
  
  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setFormError('Please fix the errors below');
      return;
    }
    
    try {
      setSaving(true);
      setFormError(null);
      
      // Create lender array for API call
      const lenderArray = [formData];
      
      const result = await saveLenderSettings(lenderArray);
      
      if (result.errors && result.errors.length > 0) {
        // Show first error
        setFormError(result.errors[0]);
        return;
      }
      
      if (result.data && result.data.length > 0) {
        // Notify parent component of successful creation
        onLenderCreated(result.data[0]);
        
        // Reset form and close modal
        resetForm();
        onHide();
      } else {
        setFormError('Failed to create lender - no data returned');
      }
      
    } catch (err) {
      console.error('Error creating lender:', err);
      setFormError(err);
      notifyError(err, 'Create Failed');
    } finally {
      setSaving(false);
    }
  };
  
  /**
   * Reset form to initial state
   */
  const resetForm = () => {
    setFormData({
      lenderName: '',
      financingType: '',
      tier: 3,
      active: true,
      emailOnly: true,
      emailToAddress: '',
      emailCcAddress: '',
      emailSubject: 'New Submission For Review - {businessName}',
      emailBody: 'Hello {lenderName},\n\nPlease see the attached submission with supporting documentation for review. Thank you.\n\n{dealNotes}\n\nSubmission Notes:\n\nThank You,\nOperations Support'
    });
    setErrors({});
    setFormError(null);
  };
  
  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!saving) {
      resetForm();
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop={saving ? 'static' : true}>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          <FaPlus className="me-2" />
          Add New Email Lender
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Form Error Alert */}
          {formError && (
            <Alert variant="danger" className="mb-4">
              <strong>Error:</strong> {formError}
            </Alert>
          )}
          
          <Row>
            {/* Basic Information */}
            <Col md={6}>
              <h6 className="mb-3">Basic Information</h6>
              
              <Form.Group className="mb-3">
                <Form.Label>Lender Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.lenderName}
                  onChange={(e) => handleFieldChange('lenderName', e.target.value)}
                  placeholder="Enter lender name"
                  isInvalid={!!errors.lenderName}
                  disabled={saving}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {errors.lenderName}
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Financing Type *</Form.Label>
                <Form.Select
                  value={formData.financingType}
                  onChange={(e) => handleFieldChange('financingType', e.target.value)}
                  isInvalid={!!errors.financingType}
                  disabled={saving}
                  required
                >
                  <option value="">Select financing type...</option>
                  <option value="working-capital">Working Capital</option>
                  <option value="equipment-financing">Equipment Financing</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.financingType}
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Priority Tier</Form.Label>
                <Form.Select
                  value={formData.tier}
                  onChange={(e) => handleFieldChange('tier', parseInt(e.target.value))}
                  disabled={saving}
                >
                  <option value={1}>Tier 1 (Highest Priority)</option>
                  <option value={2}>Tier 2 (Medium Priority)</option>
                  <option value={3}>Tier 3 (Lowest Priority)</option>
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="active-switch"
                  label="Active"
                  checked={formData.active}
                  onChange={(e) => handleFieldChange('active', e.target.checked)}
                  disabled={saving}
                />
                <Form.Text className="text-muted">
                  Inactive lenders will not appear in submission options
                </Form.Text>
              </Form.Group>
            </Col>
            
            {/* Email Configuration */}
            <Col md={6}>
              <h6 className="mb-3">Email Configuration</h6>
              
              <Form.Group className="mb-3">
                <Form.Label>Email Address *</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.emailToAddress}
                  onChange={(e) => handleFieldChange('emailToAddress', e.target.value)}
                  placeholder="lender@example.com"
                  isInvalid={!!errors.emailToAddress}
                  disabled={saving}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {errors.emailToAddress}
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>CC Email Address (Optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.emailCcAddress}
                  onChange={(e) => handleFieldChange('emailCcAddress', e.target.value)}
                  placeholder="cc1@example.com; cc2@example.com"
                  isInvalid={!!errors.emailCcAddress}
                  disabled={saving}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.emailCcAddress}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Separate multiple emails with semicolons
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          
          {/* Email Templates */}
          <Row className="mt-4">
            <Col>
              <h6 className="mb-3">Email Templates</h6>
              <p className="text-muted small">
                Available placeholders: {'{businessName}'}, {'{lenderName}'}, {'{requestedAmount}'}, {'{clientName}'}, {'{dealNotes}'}
              </p>
              
              <Form.Group className="mb-3">
                <Form.Label>Email Subject</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.emailSubject}
                  onChange={(e) => handleFieldChange('emailSubject', e.target.value)}
                  placeholder="New Submission For Review - {businessName}"
                  disabled={saving}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Email Body</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={formData.emailBody}
                  onChange={(e) => handleFieldChange('emailBody', e.target.value)}
                  placeholder="Enter email body template..."
                  disabled={saving}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <FaPlus className="me-2" />
                Create Lender
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddLenderModal;